import { FC, useEffect, useState } from "react";

import Button from "components/Button";
import InfoCard from "components/InfoCard";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { hideModal } from "store/slices/modalSlice";
import { removePortfolioRequest, resetErrorsPortfolio } from "store/slices/portfolioSlice";
import { showToast } from "store/slices/toastSlice";

import styles from "../styles.module.scss";

type PropTypes = {
  onClose?: () => void;
  id?: number;
  name?: string;
  assets?: number;
};

const ModalRemovePortfolioContent: FC<PropTypes> = ({ onClose, id, name, assets = 0 }) => {
  const dispatch = useAppDispatch();
  const { isLoading, errors } = useAppSelector((state: RootStore) => state.portfolios);
  const [submitted, setSubmitted] = useState(false);

  const assetsLabel = `${assets} ${assets === 1 ? "asset" : "assets"} will be deleted`;

  const handleDelete = () => {
    if (id === undefined) {
      return;
    }
    setSubmitted(true);
    dispatch(removePortfolioRequest(Number(id)));
  };

  const handleCancel = () => {
    onClose?.();
    dispatch(hideModal());
  };

  useEffect(() => {
    dispatch(resetErrorsPortfolio());
  }, [dispatch]);

  useEffect(() => {
    if (submitted && errors) {
      setSubmitted(false);
      dispatch(resetErrorsPortfolio());
      dispatch(
        showToast({
          id: "error-deleting-portfolio",
          message: "Failed to delete portfolio. Please try again later.",
          type: "error",
          duration: 3000,
        }),
      );
    }
  }, [submitted, errors, dispatch]);

  return (
    <div className={styles.content}>
      <p className={styles.contentTitle}>
        Are you sure you want to delete the portfolio <span>"{name}"</span>?
      </p>

      <InfoCard color="red" variant="red" size="small">
        <p className={styles.contentInfoTitle}>Warning: This portfolio contains assets</p>

        <ul className={styles.contentInfoList}>
          <li>{assetsLabel}</li>
          <li>All associated devices and incidents will be removed</li>
          <li>All historical data will be permanently lost</li>
        </ul>
      </InfoCard>

      <p>This action cannot be undone. All associated data will be permanently deleted.</p>

      <div className={styles.buttons}>
        <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleDelete} disabled={isLoading}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default ModalRemovePortfolioContent;
